/**
 * Errors produced BY the test harness rather than by the product.
 *
 * Preview carries roughly 60x production's error volume because every PR
 * preview runs the full E2E suite against a real API. That noise is not
 * harmless: Sentry's Linear rule fires on FIRST-SEEN with no threshold, so a
 * fingerprint that debuts in preview consumes its ticket there and never files
 * one when the same fault later appears in production.
 *
 * Most MESSAGE markers below are inherently a fixture artifact — a reserved
 * example domain, an `e2e_test_` job name, an `/e2e/` media path. Those are
 * identifiers the harness itself mints, so no production code path can emit
 * them and they are dropped in all environments rather than gated on one.
 *
 * `sk_test_` is the exception. A real USER cannot produce it, but a
 * MISCONFIGURATION can: a Stripe test key wired into a live secret makes
 * production reject every payment with `Invalid API Key provided: sk_test_…`.
 * That is a total payments outage wearing a fixture's clothing, so the marker
 * is gated on the environment like the route rule below.
 *
 * The `/testing/` ROUTE rule is different and is gated on the environment. It
 * drops an event on the strength of the route alone, whatever the message, so
 * in production it would be a blanket over a whole URL prefix. Those routes are
 * mounted only outside production today — but "today" is exactly the assumption
 * that goes stale silently, and the failure mode is a real incident that never
 * files a ticket. Outside production the same rule costs nothing.
 */
/** Reserved by RFC 2606 for documentation/tests; never a deliverable address. */
const FIXTURE_EMAIL_DOMAIN = '@example.com';
const FIXTURE_MARKERS = [
    // BullMQ jobs the E2E suite enqueues, e.g. `claire-classify-e2e_test_…`.
    'e2e_test_',
    // Fixture media the suite uploads/reads, e.g. S3 404 on `/e2e/procedure1.mp4`.
    '/e2e/',
];
/**
 * Seeded Stripe stub key used by the preview/E2E payments fixtures. Dropped
 * outside production only — in production this string means a test key reached
 * a live secret, which is an incident, not noise.
 */
const STUB_STRIPE_KEY_MARKER = 'sk_test_';
/** Routes that exist only to let the harness bypass flows; off in production. */
const HARNESS_ROUTE = '/testing/';
/**
 * True when the event is harness noise and should not reach Sentry.
 *
 * `transaction` is checked as well as the message because the harness routes
 * fail in many different ways (validation, provisioning races) and it is the
 * ROUTE, not the message, that marks them as ours.
 */
export function isTestFixtureNoise(input) {
    const { error, transaction, environment } = input;
    if (environment !== 'production' && transaction?.includes(HARNESS_ROUTE)) {
        return true;
    }
    const message = error instanceof Error ? error.message : '';
    if (!message)
        return false;
    if (FIXTURE_MARKERS.some((marker) => message.includes(marker)))
        return true;
    if (environment !== 'production' &&
        message.includes(STUB_STRIPE_KEY_MARKER)) {
        return true;
    }
    // Narrow on purpose: only the provider's rejection of a fixture recipient,
    // not every mention of the domain. A real user CAN type an @example.com
    // address — that send still fails and is still recorded as failed on the
    // recipient row, which is the surface that actually reports it. Dropping the
    // Sentry event loses nothing actionable.
    if (message.includes(FIXTURE_EMAIL_DOMAIN) &&
        /invalid .?to.? field/i.test(message)) {
        return true;
    }
    return false;
}
const NON_ACTIONABLE_META = new Set(['transient', 'rate_limited']);
const NOISE_MESSAGES = [
    /^connect ECONNREFUSED 127\.0\.0\.1:6380$/,
    /^Anthropic client not initialized$/,
    /^Twilio credentials not configured$/,
    /^Health check failed: database$/,
    /^the worker has exited$/i,
    /^(?:ReplyError: )?READONLY(?: |$)/,
    /^(?:ReplyError: )?ERR caller gone(?: |$)/,
    /^ResizeObserver loop (?:completed with undelivered notifications\.?|limit exceeded)$/i,
    // View Transition API aborting a `viewTransition: true` navigation when the
    // tab is hidden or resized mid-navigation. Unhandled rejection, no stack.
    // Only these two browser-given reasons: the bare "invalid state" form is
    // also what a duplicate `view-transition-name` on our own page produces.
    /^(?:Error: )?InvalidStateError: Transition was aborted because of invalid state\. (?:Document hidden|Viewport size changed)$/,
    // iOS PiP tapped before the video track loaded (Low Power Mode). Thrown by
    // media-chrome's requestPictureInPicture(); nothing to act on.
    /^InvalidStateError: The video element does not have a video track/,
];
/**
 * A dropped pooled connection can leave postgres.js with `socket = null` while
 * a write is still queued; `nextWrite` then throws a bare TypeError from a
 * `setImmediate` callback, outside any query try/catch. Same signature as
 * `isRecoverableDbConnectionError` in apps/api/src/main.ts (and the
 * video-worker's copy), which survive it on purpose: the message AND a
 * postgres internals frame. The message alone is a generic null-deref that app
 * code can throw, so it is not enough.
 */
const POSTGRES_NULL_SOCKET_MESSAGE = /^(?:TypeError: )?Cannot read properties of null \(reading '(?:write|read|readable|writable|once|on|end)'\)$/;
const POSTGRES_PACKAGE_FRAME = /node_modules[\\/].*postgres[\\/]/;
const POSTGRES_WRITE_FRAME = /connection\.js|nextWrite/;
const isPostgresInternalFrame = (frame) => POSTGRES_PACKAGE_FRAME.test(frame) && POSTGRES_WRITE_FRAME.test(frame);
const isPostgresNullSocketError = (message, frames) => typeof message === 'string' &&
    POSTGRES_NULL_SOCKET_MESSAGE.test(message) &&
    frames.some(isPostgresInternalFrame);
/** One line per Sentry/PostHog frame, shaped like a V8 `at fn (file)` line. */
const framesOf = (value) => {
    const frames = value
        ?.stacktrace?.frames;
    if (!Array.isArray(frames))
        return [];
    return frames.map((frame) => `${frame?.function ?? ''} ${frame?.filename ?? ''}`.trim());
};
const hasNoiseFlag = (event, hint, flag) => event.properties?.[flag] === true ||
    event.extra?.[flag] === true ||
    errorNoiseProperties(hint?.originalException)[flag] === true;
/** Preserve only classification metadata, never credentials or the Error object. */
export function errorNoiseProperties(error) {
    const seen = new Set();
    let current = error;
    while (current &&
        typeof current === 'object' &&
        !seen.has(current) &&
        seen.size < 10) {
        seen.add(current);
        const candidate = current;
        if (candidate.name === 'MetaApiError' &&
            (candidate.isExpected === true ||
                (typeof candidate.category === 'string' &&
                    NON_ACTIONABLE_META.has(candidate.category)))) {
            return { metaErrorNonActionable: true };
        }
        // body-parser/raw-body: the client closed the connection mid-upload. It is
        // a 400 `BadRequestError` that is not an HttpException, so the API filter
        // reports it as an unhandled 500. Kept, but marked expected (see
        // markExpectedEvent).
        if (candidate.type === 'request.aborted') {
            return { requestAborted: true };
        }
        current = candidate.cause;
    }
    return {};
}
/** One browser-safe policy for both SDKs. Product analytics always pass. */
export function shouldDropEvent(event, hint) {
    if (event.event && event.event !== '$exception')
        return false;
    const properties = event.properties ?? {};
    if (hasNoiseFlag(event, hint, 'metaErrorNonActionable'))
        return true;
    const environment = event.environment ??
        (typeof properties.environment === 'string'
            ? properties.environment
            : undefined);
    const transaction = event.transaction ??
        (typeof properties.path === 'string' ? properties.path : undefined);
    const messages = [];
    if (typeof event.message === 'string')
        messages.push(event.message);
    const values = event.exception?.values ?? properties.$exception_list;
    if (Array.isArray(values)) {
        for (const value of values)
            if (value && typeof value.value === 'string')
                messages.push(value.value);
    }
    if (!messages.length && Array.isArray(properties.$exception_values)) {
        messages.push(...properties.$exception_values.filter((v) => typeof v === 'string'));
    }
    let error = hint?.originalException;
    const seen = new Set();
    while (error instanceof Error && !seen.has(error) && seen.size < 10) {
        seen.add(error);
        messages.push(error.message);
        error = error.cause;
    }
    if (isTestFixtureNoise({ error: undefined, environment, transaction }))
        return true;
    return messages.some((message) => message === 'Invalid password' ||
        NOISE_MESSAGES.some((pattern) => pattern.test(message)) ||
        isTestFixtureNoise({ error: new Error(message), environment }));
}
const EXPECTED_PREFIX = 'Expected behaviour: ';
/**
 * The exception an event reports. Sentry lists the cause chain oldest first,
 * so it is the LAST value; PostHog lists the thrown error first, then causes.
 */
function reportedException(event) {
    const sentry = event.exception?.values;
    const posthog = event.properties?.$exception_list;
    const reported = Array.isArray(sentry)
        ? sentry[sentry.length - 1]
        : Array.isArray(posthog)
            ? posthog[0]
            : undefined;
    return reported && typeof reported === 'object'
        ? reported
        : undefined;
}
/**
 * The postgres.js null-socket write as the reported error itself. An error
 * that only has it as a cause is a request or job that really failed, and is
 * reported as such. Sentry's `logError` captures the deepest cause and records
 * the wrapper as `wrapperMessage` (or `dbErrorQuery`), which is how that case
 * is told apart when the null write is the `originalException`.
 */
function isUnwrappedNullSocketWrite(event, hint) {
    if (event.extra &&
        ('wrapperMessage' in event.extra || 'dbErrorQuery' in event.extra))
        return false;
    const original = hint?.originalException;
    if (original instanceof Error) {
        return isPostgresNullSocketError(original.message, (original.stack ?? '').split('\n'));
    }
    const reported = reportedException(event);
    return isPostgresNullSocketError(reported?.value, framesOf(reported));
}
/**
 * Handled conditions that stay reported but read as expected, the way the
 * call-site reports from #1689 do: same level, the message prefixed
 * "Expected behaviour: " with the real message kept after it (so each still
 * groups on its own), and `expected: 'true'` as a Sentry tag or PostHog
 * property. These two are raised where no call site can label them (an SDK
 * global handler, the API's catch-all filter), so the event is labelled here.
 * Mutates and returns the same event: some callers keep their own reference.
 */
function markExpectedEvent(event, hint) {
    if (event.event && event.event !== '$exception')
        return event;
    const reason = hasNoiseFlag(event, hint, 'requestAborted')
        ? 'client closed the connection mid-request'
        : isUnwrappedNullSocketWrite(event, hint)
            ? 'database connection dropped before a queued write, the pool reconnects'
            : undefined;
    if (!reason)
        return event;
    const target = event;
    const label = (message) => message.startsWith(EXPECTED_PREFIX)
        ? message
        : `${EXPECTED_PREFIX}${reason}: ${message}`;
    const reported = reportedException(target);
    if (typeof reported?.value === 'string') {
        reported.value = label(reported.value);
    }
    else if (typeof target.message === 'string') {
        target.message = label(target.message);
    }
    if (target.event === '$exception') {
        target.properties = { ...target.properties, expected: 'true' };
    }
    else {
        target.tags = { ...target.tags, expected: 'true' };
    }
    return event;
}
/**
 * SDK-compatible adapter; both initializers reference this same symbol. Drops
 * noise, and labels the expected conditions it keeps (see markExpectedEvent).
 */
export function filterNoiseEvent(event, hint) {
    if (!event || shouldDropEvent(event, hint))
        return null;
    return markExpectedEvent(event, hint);
}
//# sourceMappingURL=should-drop-event.js.map